import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLiveSessionJoin, recordLiveSessionAttendance } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Loader2, Radio, Video } from 'lucide-react';

const EMPTY = { loading: true, error: '', session: null };

/**
 * The in-portal room for one live class.
 *
 * A Zoom-provisioned session renders inside the portal through the Meeting SDK.
 * Any other pasted link — Meet, Teams, a personal Zoom room — is launched
 * externally, but labelled with the vendor it actually belongs to rather than
 * being called "Zoom" regardless.
 *
 * Either way, arriving here counts as attending: the server records the
 * caller present against the same register the manual roll-call writes to.
 */
export default function LiveMeetingRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const clientRef = useRef(null);
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let session = null;
      try {
        const { data } = await getLiveSessionJoin(sessionId);
        session = data;
        if (cancelled) return;

        // Attendance is best effort — a failure here must never keep someone
        // out of their own class.
        recordLiveSessionAttendance(sessionId).catch(() => {});

        if (data.provider !== 'zoom') {
          setState({
            loading: false,
            error: data.join_url ? '' : 'No meeting link has been added to this session yet.',
            session: data,
          });
          return;
        }

        const module = await import('@zoom/meetingsdk/embedded');
        if (cancelled || !rootRef.current) return;
        const client = module.default.createClient();
        clientRef.current = client;
        await client.init({
          zoomAppRoot: rootRef.current,
          language: 'en-US',
          leaveOnPageUnload: true,
          patchJsMedia: true,
        });
        await client.join({
          sdkKey: data.sdk_key,
          signature: data.signature,
          meetingNumber: String(data.meeting_number),
          password: data.password || '',
          userName: data.user_name,
          userEmail: data.user_email || '',
          ...(data.zak ? { zak: data.zak } : {}),
        });
        if (!cancelled) setState({ loading: false, error: '', session: data });
      } catch (err) {
        if (!cancelled) setState({
          loading: false,
          error: err?.response?.data?.detail || err?.message || 'Could not join this meeting.',
          session,
        });
      }
    })();
    return () => {
      cancelled = true;
      if (clientRef.current?.leaveMeeting) clientRef.current.leaveMeeting().catch(() => {});
    };
  }, [sessionId]);

  const session = state.session;
  const isExternal = session && session.provider !== 'zoom' && session.join_url;
  const providerLabel = session?.provider_label || 'the meeting link';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-[#062B1A]">{session?.title || 'Live class'}</h2>
            {session?.status === 'live' && (
              <Badge className="border-0 bg-green-100 text-[10px] text-green-700">
                <Radio className="mr-1 size-2.5" />Live now
              </Badge>
            )}
          </div>
          <p className="text-sm text-[#062B1A]/55">Hosted securely inside MCC Portal</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Leave room</Button>
      </div>

      {state.loading && (
        <div className="flex min-h-[520px] items-center justify-center rounded-xl bg-white">
          <Loader2 className="size-8 animate-spin text-[#D4AF37]" />
        </div>
      )}

      {state.error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700">{state.error}</div>
      )}

      {isExternal && (
        <div className="rounded-xl border bg-white p-8 text-center">
          <Video className="mx-auto mb-3 size-10 text-[#D4AF37]" />
          <p className="mb-1 text-sm font-semibold text-[#062B1A]">This class runs in {providerLabel}.</p>
          <p className="mb-4 text-sm text-[#062B1A]/60">
            Your attendance has been recorded. Open the meeting to join the class.
          </p>
          <Button
            onClick={() => window.open(session.join_url, '_blank', 'noopener,noreferrer')}
            className="bg-[#062B1A] text-white"
          >
            <ExternalLink className="mr-2 size-4" />
            Open {providerLabel}
          </Button>
        </div>
      )}

      {session?.recording_url && (
        <div className="flex items-center justify-between gap-3 rounded-xl border bg-white p-4">
          <p className="text-sm text-[#062B1A]/70">A recording of this class is available.</p>
          <Button
            variant="outline"
            onClick={() => window.open(session.recording_url, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink className="mr-2 size-4" />Watch replay
          </Button>
        </div>
      )}

      <div
        ref={rootRef}
        className={state.loading || state.error || isExternal ? 'hidden' : 'min-h-[680px] overflow-hidden rounded-xl bg-[#111]'}
      />
    </div>
  );
}
